import { useMemo } from "react";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { SendgridMessageEventType } from "shared";
import { When } from "react-if";
import {
  EnvelopeIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/20/solid";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { Tooltip } from "@mui/material";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";

import { Card } from "../../../../common/Atoms/Card";
import { MultiButtonCardHeader } from "../../../../common/Atoms/MultiButtonCardHeader";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { useFetchEmailMetricsQuery } from "../../../services/api/organisationApi/organisation";
import { LoadingOverlay } from "../../Molecules/LoadingOverlay";
import { Badge } from "../../../../common/Atoms/Badge";
import { HelpIcon } from "../../Molecules/HelpIcon";

import { paidLateColor, paidOnTimeColor } from "./InvoiceHistory";

const trackColor = `#e8eef0`;

// Failure event types shown in the breakdown + attention feed.
const failureLabels = {
  [SendgridMessageEventType.Bounce]: `Bounced`,
  [SendgridMessageEventType.Dropped]: `Blocked`,
  [SendgridMessageEventType.SpamReport]: `Marked as spam`,
};

export function EmailDeliveryWidget() {
  const { organisationId } = useParams<{ organisationId: string }>();

  const { data: emailMetrics, isLoading } = useFetchEmailMetricsQuery({ orgId: organisationId }, {
    skip: !organisationId,
    refetchOnMountOrArgChange: true,
  });

  const stats = useMemo(() => {
    const totals = emailMetrics?.totals ?? {};

    const bounced = totals[SendgridMessageEventType.Bounce] ?? 0;
    const blocked = totals[SendgridMessageEventType.Dropped] ?? 0;
    const spam = totals[SendgridMessageEventType.SpamReport] ?? 0;

    // Totals bucket each message by its *latest* event, so an opened or clicked
    // email is no longer counted as `delivered`. Anything that isn't a failure
    // counts as a success — including `processed` emails still in flight.
    const delivered = (totals[SendgridMessageEventType.Delivered] ?? 0)
      + (totals[SendgridMessageEventType.Open] ?? 0)
      + (totals[SendgridMessageEventType.Click] ?? 0)
      + (totals[SendgridMessageEventType.Processed] ?? 0);

    const failed = bounced + blocked + spam;
    const attempts = delivered + failed;
    const health = attempts ? Math.round((delivered / attempts) * 100) : 100;

    const breakdown = [
      { label: failureLabels[SendgridMessageEventType.Bounce], count: bounced },
      { label: failureLabels[SendgridMessageEventType.Dropped], count: blocked },
      { label: failureLabels[SendgridMessageEventType.SpamReport], count: spam },
    ];

    return {
      delivered,
      failed,
      attempts,
      health,
      breakdown,
      hasData: Object.values(totals).some(v => v > 0),
    };
  }, [emailMetrics]);

  // Health arc colour: green when clean, amber for degraded, red when poor.
  const healthColor = stats.health >= 90 ? paidOnTimeColor : stats.health >= 70 ? `#e9a23b` : paidLateColor;

  const recentFailures = emailMetrics?.recentFailures ?? [];
  const latestFailure = recentFailures[0];

  const reportUrl = `/reports/email?org=${organisationId}`;

  return (
    <LoadingOverlay loading={ isLoading }>
      <Card className={ `min-h-full min-w-full` }
        variant={ `soft` }>
        <MultiButtonCardHeader
          title={ `Email Delivery` }
          description={ `Quick insights into any recent email delivery issues` }
          variant={ `plain` }
          cta={
            <Link
              to={ reportUrl }
              className={ `text-sm font-medium` }
            >
              { `View full report` }
            </Link>
          }
        />

        <When condition={ !stats.hasData }>
          <div className={ `min-h-[200px] h-full flex flex-col items-center justify-center text-center` }>
            <EnvelopeIcon className={ `w-12 h-12 mx-auto text-gray-300` } />
            <Paragraph variant={ `secondary` }>
              { `Email delivery data will appear here once you start sending emails.` }
            </Paragraph>
          </div>
        </When>

        <When condition={ stats.hasData }>
          <div className={ `mt-4 flex flex-col gap-8 sm:flex-row sm:items-start` }>

            { /* Health gauge */ }
            <div className={ `flex flex-none flex-col items-center sm:w-[200px]` }>
              <Tooltip title={ `${stats.delivered} of ${stats.attempts} email${stats.attempts === 1 ? `` : `s`} delivered` }>
                <div className={ `relative h-[150px] w-[200px]` }>
                  <Gauge
                    value={ stats.health }
                    startAngle={ -110 }
                    endAngle={ 110 }
                    innerRadius={ `72%` }
                    outerRadius={ `100%` }
                    cornerRadius={ 4 }
                    text={ `` }
                    sx={ {
                      [`& .${gaugeClasses.valueArc}`]: { fill: healthColor },
                      [`& .${gaugeClasses.referenceArc}`]: { fill: trackColor },
                    } }
                  />
                  <div className={ `absolute inset-0 flex items-center justify-center pt-6 pointer-events-none` }>
                    <div className={ `text-3xl font-bold leading-none text-gray-800` }>{ stats.health }<span className={ `text-xl` }>{ `%` }</span></div>
                  </div>
                </div>
              </Tooltip>
              <div className={ `text-xs text-gray-500` }>{ `Delivery health · last 90 days` }</div>

              <When condition={ stats.failed > 0 }>
                <Popover className={ `relative mt-3` }>
                  <PopoverButton className={ `inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-600 focus:outline-none` }>
                    { `${stats.failed} failed email${stats.failed === 1 ? `` : `s`}` }
                    <ChevronDownIcon className={ `h-3 w-3` } />
                  </PopoverButton>
                  <PopoverPanel
                    anchor={ `bottom` }
                    className={ `z-20 w-64 rounded-lg border border-gray-200 bg-white p-3.5 shadow-xl [--anchor-gap:8px]` }
                  >
                    <div className={ `text-[11px] font-semibold uppercase tracking-wide text-gray-400` }>{ `Failures · last 90 days` }</div>
                    <div className={ `mt-1.5 flex flex-col` }>
                      { stats.breakdown.map(row => (
                        <div
                          key={ row.label }
                          className={ `flex items-center gap-2 border-b border-gray-100 py-1.5 last:border-0` }
                        >
                          <span className={ `h-2 w-2 rounded-full ${row.count > 0 ? `bg-red-500` : `bg-gray-300`}` } />
                          <span className={ `flex-1 text-sm text-gray-600` }>{ row.label }</span>
                          <span className={ `text-sm font-bold ${row.count > 0 ? `text-red-600` : `text-gray-400`}` }>{ row.count }</span>
                        </div>
                      )) }
                    </div>
                    <div className={ `mt-2 flex items-center justify-between border-t border-gray-100 pt-2.5` }>
                      <span className={ `text-xs text-gray-400` }>
                        { latestFailure?.date ? `Latest: ${DateTime.fromJSDate(new Date(latestFailure.date)).toRelative()}` : `` }
                      </span>
                      <Link
                        to={ reportUrl }
                        className={ `text-xs font-medium` }
                      >
                        { `View full report` }
                      </Link>
                    </div>
                  </PopoverPanel>
                </Popover>
              </When>
            </div>

            { /* Needs attention feed */ }
            <div className={ `flex min-w-0 flex-1 flex-col gap-2.5` }>
              <When condition={ recentFailures.length > 0 }>
                <div className={ `text-[11px] font-semibold uppercase tracking-wide text-gray-400` }>{ `Needs attention` }</div>
                { recentFailures.map((row, idx) => (
                  <div
                    key={ `email-fail-${idx}` }
                    className={ `rounded-lg border border-red-200 bg-red-50 px-3.5 py-3` }
                  >
                    <div className={ `flex items-center gap-2` }>
                      <span className={ `min-w-0 flex-1 truncate text-sm font-semibold text-gray-800` }>{ row.email }</span>
                      <Badge color={ `red` }
                        size={ `xs` }>
                        { failureLabels[row.eventType] ?? row.eventType }
                      </Badge>
                    </div>
                    <When condition={ !!row.reason }>
                      <div className={ `mt-1 flex min-w-0 items-center text-xs text-gray-500` }>
                        <span className={ `min-w-0 truncate` }>{ row.reason }</span>
                        <HelpIcon tooltip={ row.reason } />
                      </div>
                    </When>
                    <When condition={ !!row.date }>
                      <Tooltip title={ DateTime.fromJSDate(new Date(row.date)).toFormat(`yyyy-MM-dd HH:mm:ss`) }>
                        <span className={ `mt-1 inline-block text-xs text-gray-400` }>
                          { DateTime.fromJSDate(new Date(row.date)).toRelative() }
                        </span>
                      </Tooltip>
                    </When>
                  </div>
                )) }
              </When>

              <When condition={ stats.delivered > 0 }>
                <div className={ `flex items-center gap-2 rounded-lg border border-gray-200 px-3.5 py-2.5` }>
                  <span className={ `flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-100 text-emerald-600` }>
                    <CheckIcon className={ `h-3 w-3` } />
                  </span>
                  <span className={ `text-sm text-gray-600` }>
                    { `${stats.delivered} ${recentFailures.length > 0 ? `other ` : ``}email${stats.delivered === 1 ? `` : `s`} delivered successfully` }
                  </span>
                </div>
              </When>
            </div>
          </div>
        </When>
      </Card>
    </LoadingOverlay>
  );
}
